import React, { useEffect, useState } from "react";
import { supabase } from "../config/supabase";
import { useNavigate } from "react-router-dom";
import {
  sanitizeError,
  isValidEmail,
  isValidPassword,
  getPasswordValidationError,
  globalRateLimiter,
} from "../utils/security";
import { logger } from "../utils/logger";
import MobileAppRedirect from "./MobileAppRedirect";

interface LoginForm {
  email: string;
  password: string;
}

const initialLoginState: LoginForm = {
  email: "",
  password: "",
};

const LoginPage: React.FC = () => {
  const [loginForm, setLoginForm] = useState<LoginForm>(initialLoginState);
  const [loginStatus, setLoginStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>("");
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState<string>("");
  const [forgotCooldown, setForgotCooldown] = useState<number>(0);
  const [showRegister, setShowRegister] = useState<boolean>(false);
  const [registerForm, setRegisterForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [registerStatus, setRegisterStatus] = useState<string>("");
  const [oauthLoading, setOauthLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  // Open registration view if ?register=1 is present
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      if (params.get("register") === "1") {
        setShowRegister(true);
      }
    } catch {}
  }, []);

  // Input validation using security utilities
  const validateEmail = isValidEmail;

  // Login handler
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginStatus("");

    // Rate limiting check
    const loginKey = `login_${loginForm.email}`;
    if (!globalRateLimiter.canAttempt(loginKey, 5, 15 * 60 * 1000)) {
      setLoginStatus("Too many login attempts. Please try again later.");
      setLoading(false);
      return;
    }

    // Validate email
    if (!validateEmail(loginForm.email)) {
      setLoginStatus("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginForm.email.trim().toLowerCase(),
        password: loginForm.password,
      });

      if (error) throw error;

      // Check if email is confirmed - this is the key fix
      if (data.user && !data.user.email_confirmed_at) {
        // Sign out the user immediately since they shouldn't be logged in
        await supabase.auth.signOut();
        setLoginStatus(
          "Please check your email and confirm your account before logging in. If you haven't received the confirmation email, please check your spam folder or contact support."
        );
        setLoading(false);
        return;
      }

      // Reset rate limiter on success
      globalRateLimiter.reset(loginKey);

      setLoginStatus("Login successful!");
      setLoginForm(initialLoginState);
      // Redirect to dashboard after successful login
      setTimeout(() => {
        navigate("/dashboard");
      }, 500);
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setLoginStatus(safeError);
    } finally {
      setLoading(false);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (forgotCooldown > 0) return;
    setLoading(true);
    setForgotPasswordStatus("");

    // Validate email
    if (!validateEmail(forgotPasswordEmail)) {
      setForgotPasswordStatus("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      // Check if the email exists in user_data table before sending reset
      const { data: existingUser, error: lookupError } = await supabase
        .from("user_data")
        .select("email")
        .ilike("email", forgotPasswordEmail.trim().toLowerCase())
        .limit(1)
        .maybeSingle();

      if (lookupError) {
        throw lookupError;
      }

      if (!existingUser) {
        setForgotPasswordStatus("User not found.");
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(
        forgotPasswordEmail.trim().toLowerCase(),
        {
          // Ask Supabase to deliver hash tokens for broader compatibility
          redirectTo: `${window.location.origin}/reset-password#provider=recovery`,
        }
      );

      if (error) throw error;

      setForgotPasswordStatus(
        "Password reset instructions have been sent to your email address."
      );
      setForgotPasswordEmail("");
      // Start 60s resend cooldown
      setForgotCooldown(60);
      // Immediately take user to the OTP entry page
      navigate("/reset-password");
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setForgotPasswordStatus(safeError);
    } finally {
      setLoading(false);
    }
  };

  // Cooldown timer for forgot password
  React.useEffect(() => {
    if (forgotCooldown <= 0) return;
    const t = setInterval(() => {
      setForgotCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [forgotCooldown]);

  // Google OAuth sign-in
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setLoginStatus("");
    setOauthLoading(true);
    try {
      // Determine redirect URL based on environment
      let redirectUrl: string;
      const hostname = window.location.hostname;

      if (hostname === "amblyfy.com" || hostname === "www.amblyfy.com") {
        redirectUrl = "https://amblyfy.com/dashboard";
      } else if (hostname === "localhost" || hostname === "127.0.0.1") {
        // For localhost development
        redirectUrl = `http://${hostname}:${window.location.port}/dashboard`;
      } else {
        // For other development/staging environments
        redirectUrl = `${window.location.origin}/dashboard`;
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });
      if (error) throw error;
      // The redirect will happen automatically
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setLoginStatus(safeError);
      setOauthLoading(false);
      setLoading(false);
    }
  };

  // Apple OAuth sign-in
  const handleAppleSignIn = async () => {
    setLoading(true);
    setLoginStatus("");
    setOauthLoading(true);
    try {
      let redirectUrl: string;
      const hostname = window.location.hostname;

      if (hostname === "amblyfy.com" || hostname === "www.amblyfy.com") {
        redirectUrl = "https://amblyfy.com/dashboard";
      } else if (hostname === "localhost" || hostname === "127.0.0.1") {
        redirectUrl = `http://${hostname}:${window.location.port}/dashboard`;
      } else {
        redirectUrl = `${window.location.origin}/dashboard`;
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: {
          redirectTo: redirectUrl,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      const safeError = sanitizeError(err);
      setLoginStatus(safeError);
      setOauthLoading(false);
      setLoading(false);
    }
  };

  // Registration handler
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setRegisterStatus("");

    if (!validateEmail(registerForm.email)) {
      setRegisterStatus("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    if (!isValidPassword(registerForm.password)) {
      const passwordError = getPasswordValidationError(registerForm.password);
      setRegisterStatus(
        passwordError || "Password does not meet requirements."
      );
      setLoading(false);
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterStatus("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      logger.log("Attempting registration");

      const { data, error } = await supabase.auth.signUp({
        email: registerForm.email.trim().toLowerCase(),
        password: registerForm.password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });

      if (error) throw error;

      logger.log("Registration successful");

      // Always require email confirmation
      if (data.user) {
        setRegisterStatus(
          "Registration successful! Please check your email to confirm your account. You must confirm your email before you can log in. If you don't see the email, please check your spam folder."
        );
        setRegisterForm({ email: "", password: "", confirmPassword: "" });
      } else {
        setRegisterStatus("Registration failed. Please try again.");
      }
    } catch (err: any) {
      logger.error("Registration error:", err);
      if (err.message && err.message.toLowerCase().includes("network")) {
        setRegisterStatus(
          "Registration failed. Please check your internet connection or try again later."
        );
      } else if (
        err.message &&
        err.message.toLowerCase().includes("already registered")
      ) {
        setRegisterStatus(
          "This email is already registered. Please try logging in instead."
        );
      } else {
        setRegisterStatus(sanitizeError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MobileAppRedirect />
      <div className="min-h-screen bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green flex items-center justify-center py-6 px-2 sm:px-6 lg:px-8">
        <div className="w-full max-w-md sm:max-w-lg bg-white rounded-2xl shadow-2xl p-4 sm:p-8 space-y-8 mx-auto">
          {/* Header */}
          <div className="text-center">
            <a href="/" className="inline-block mb-6">
              <img
                src="/assets/Amblify_logo_zilsfix.png"
                alt="Amblyfy"
                className="h-12 mx-auto"
              />
            </a>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-brand-dark-blue mb-2">
              Welcome to Amblyfy
            </h2>
            <p className="text-gray-700 opacity-90 text-sm sm:text-base">
              {showRegister
                ? "Create a new account"
                : "Sign in to your account"}
            </p>
          </div>

          {/* Login or Register Form */}
          <div className="bg-gray-50 rounded-lg shadow p-4 sm:p-8">
            {showRegister ? (
              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label
                    htmlFor="register-email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    id="register-email"
                    type="email"
                    placeholder="Enter your email"
                    maxLength={254}
                    value={registerForm.email}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        email: e.target.value,
                      })
                    }
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                  />
                </div>
                <div>
                  <label
                    htmlFor="register-password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="register-password"
                    type="password"
                    placeholder="Enter your password"
                    maxLength={128}
                    value={registerForm.password}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        password: e.target.value,
                      })
                    }
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                  />
                </div>
                <div>
                  <label
                    htmlFor="register-confirm-password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Confirm Password
                  </label>
                  <input
                    id="register-confirm-password"
                    type="password"
                    placeholder="Confirm your password"
                    maxLength={128}
                    value={registerForm.confirmPassword}
                    onChange={(e) =>
                      setRegisterForm({
                        ...registerForm,
                        confirmPassword: e.target.value,
                      })
                    }
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base"
                >
                  {loading ? "Registering..." : "Register"}
                </button>
                {registerStatus && (
                  <div
                    className={`p-3 rounded-lg text-sm font-medium mt-2 ${
                      registerStatus.startsWith("Registration successful")
                        ? "bg-green-50 text-green-800 border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                  >
                    {registerStatus}
                  </div>
                )}
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label
                    htmlFor="login-email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    placeholder="Enter your email"
                    maxLength={254}
                    value={loginForm.email}
                    onChange={(e) =>
                      setLoginForm({ ...loginForm, email: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                  />
                </div>
                <div>
                  <label
                    htmlFor="login-password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="login-password"
                    type="password"
                    placeholder="Enter your password"
                    maxLength={128}
                    value={loginForm.password}
                    onChange={(e) =>
                      setLoginForm({ ...loginForm, password: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || oauthLoading}
                  className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Signing in...
                    </div>
                  ) : (
                    "Sign In"
                  )}
                </button>
                {loginStatus && (
                  <div
                    className={`p-3 rounded-lg text-sm font-medium mt-2 ${
                      loginStatus.startsWith("Login successful")
                        ? "bg-green-50 text-green-800 border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                  >
                    {loginStatus}
                  </div>
                )}

                {/* OAuth Section */}
                <div className="space-y-3 mt-5">
                  <div className="relative flex items-center">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <span className="flex-shrink mx-4 text-gray-500 text-sm">
                      OR
                    </span>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={loading || oauthLoading}
                    className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg shadow-sm hover:shadow-md hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {oauthLoading ? (
                      <svg
                        className="animate-spin h-5 w-5 text-gray-700"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        <span>Continue with Google</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleAppleSignIn}
                    disabled={loading || oauthLoading}
                    className="w-full flex items-center justify-center gap-3 bg-black text-white font-semibold py-3 px-4 rounded-lg shadow-sm hover:shadow-md hover:bg-gray-900 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {oauthLoading ? (
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        <span>Continue with Apple</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Registration/Login Toggle Link */}
            <div className="text-center mt-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowRegister((r) => !r);
                  setRegisterStatus("");
                  setLoginStatus("");
                }}
                className="text-sm text-brand-cyan hover:text-brand-dark-blue transition-colors underline font-medium"
              >
                {showRegister
                  ? "Already have an account? Login"
                  : "New to Amblyfy? Create Account"}
              </button>
            </div>

            {/* Forgot Password Link (only show on login, below register link) */}
            {!showRegister && (
              <div className="text-center mt-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-brand-cyan hover:text-brand-dark-blue transition-colors underline"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </div>

          {/* Back to Home Link */}
          <div className="text-center">
            <a
              href="/"
              className="text-brand-dark-blue hover:text-brand-cyan transition-colors text-sm font-medium"
            >
              ← Back to Home
            </a>
          </div>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-brand-dark-blue">
                    Reset Password
                  </h3>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordStatus("");
                      setForgotPasswordEmail("");
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label
                      htmlFor="forgot-email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email Address
                    </label>
                    <input
                      id="forgot-email"
                      type="email"
                      placeholder="Enter your email address"
                      maxLength={254}
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || forgotCooldown > 0}
                    className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-medium py-2 px-4 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading
                      ? "Sending..."
                      : forgotCooldown > 0
                      ? `Resend available in ${forgotCooldown}s`
                      : "Send Reset Instructions"}
                  </button>

                  {forgotPasswordStatus && (
                    <div
                      className={`p-3 rounded-lg text-sm font-medium ${
                        forgotPasswordStatus.includes("sent")
                          ? "bg-green-50 text-green-800 border border-green-200"
                          : "bg-red-50 text-red-800 border border-red-200"
                      }`}
                    >
                      {forgotPasswordStatus}
                    </div>
                  )}

                  {/* Quick link to OTP page after sending */}
                  {forgotPasswordStatus.includes("sent") && (
                    <div className="text-center">
                      <a
                        href="/reset-password"
                        className="inline-block mt-2 text-sm text-brand-cyan hover:text-brand-dark-blue underline"
                      >
                        Enter 6-digit code
                      </a>
                    </div>
                  )}
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LoginPage;
